import { Component } from '@theme/component';

/**
 * @typedef {object} PromoProductCarouselRefs
 * @property {HTMLElement} [track]
 * @property {HTMLElement} [dots]
 * @property {HTMLButtonElement} [prev]
 * @property {HTMLButtonElement} [next]
 */

/**
 * Split promo + product carousel with pagination dots.
 * @extends {Component<PromoProductCarouselRefs>}
 */
class PromoProductCarousel extends Component {
  /** @type {number} */
  #activeIndex = 0;

  /** @type {number} */
  #raf = 0;

  connectedCallback() {
    super.connectedCallback();
    this.#buildDots();
    this.#updateUI();
    this.refs.track?.addEventListener('scroll', this.#onScroll, { passive: true });
    window.addEventListener('resize', this.#onResize, { passive: true });
  }

  disconnectedCallback() {
    this.refs.track?.removeEventListener('scroll', this.#onScroll);
    window.removeEventListener('resize', this.#onResize);
    if (this.#raf) cancelAnimationFrame(this.#raf);
    super.disconnectedCallback();
  }

  /** @param {Event} [_event] */
  previous(_event) {
    this.#scrollBy(-1);
  }

  /** @param {Event} [_event] */
  next(_event) {
    this.#scrollBy(1);
  }

  /**
   * @param {Event} event
   */
  goToDot = (event) => {
    const button = event.currentTarget;
    if (!(button instanceof HTMLButtonElement)) return;

    const index = Number(button.dataset.index);
    if (Number.isNaN(index)) return;

    this.#scrollToIndex(index);
  };

  #onScroll = () => {
    if (this.#raf) cancelAnimationFrame(this.#raf);
    this.#raf = requestAnimationFrame(() => {
      this.#updateActiveFromScroll();
      this.#updateUI();
    });
  };

  #onResize = () => {
    this.#buildDots();
    this.#updateUI();
  };

  /** @returns {HTMLElement[]} */
  #cards() {
    const track = this.refs.track;
    if (!(track instanceof HTMLElement)) return [];
    return Array.from(track.querySelectorAll('.promo-pc__card'));
  }

  /** @returns {number} */
  #pageCount() {
    const track = this.refs.track;
    if (!(track instanceof HTMLElement)) return 0;

    const cards = this.#cards();
    if (cards.length === 0) return 0;

    const cardWidth = cards[0].offsetWidth;
    const gap = Number.parseFloat(getComputedStyle(track).columnGap || getComputedStyle(track).gap || '16') || 16;
    const visible = Math.max(1, Math.round((track.clientWidth + gap) / (cardWidth + gap)));
    return Math.max(1, cards.length - visible + 1);
  }

  #buildDots() {
    const dots = this.refs.dots;
    if (!(dots instanceof HTMLElement)) return;

    const count = this.#pageCount();
    dots.replaceChildren();

    if (count <= 1) {
      dots.hidden = true;
      return;
    }

    dots.hidden = false;

    for (let i = 0; i < count; i++) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'promo-pc__dot';
      button.dataset.index = String(i);
      button.setAttribute('aria-label', `Go to slide ${i + 1}`);
      button.addEventListener('click', this.goToDot);
      dots.appendChild(button);
    }

    this.#activeIndex = Math.min(this.#activeIndex, count - 1);
  }

  /**
   * @param {number} direction
   */
  #scrollBy(direction) {
    this.#scrollToIndex(this.#activeIndex + direction);
  }

  /**
   * @param {number} index
   */
  #scrollToIndex(index) {
    const track = this.refs.track;
    const cards = this.#cards();
    if (!(track instanceof HTMLElement) || cards.length === 0) return;

    const pageCount = this.#pageCount();
    const clamped = Math.max(0, Math.min(index, pageCount - 1));
    const card = cards[clamped];
    if (!(card instanceof HTMLElement)) return;

    track.scrollTo({ left: card.offsetLeft - track.offsetLeft, behavior: 'smooth' });
    this.#activeIndex = clamped;
    this.#updateUI();
  }

  #updateActiveFromScroll() {
    const track = this.refs.track;
    const cards = this.#cards();
    if (!(track instanceof HTMLElement) || cards.length === 0) return;

    const scrollLeft = track.scrollLeft;
    let closest = 0;
    let closestDist = Infinity;

    cards.forEach((card, index) => {
      const dist = Math.abs(card.offsetLeft - track.offsetLeft - scrollLeft);
      if (dist < closestDist) {
        closestDist = dist;
        closest = index;
      }
    });

    const pageCount = this.#pageCount();
    this.#activeIndex = Math.min(closest, pageCount - 1);
  }

  #updateUI() {
    const track = this.refs.track;
    if (!(track instanceof HTMLElement)) return;

    const maxScroll = track.scrollWidth - track.clientWidth;
    const atStart = track.scrollLeft <= 2;
    const atEnd = track.scrollLeft >= maxScroll - 2;
    const pageCount = this.#pageCount();

    const prev = this.refs.prev;
    const next = this.refs.next;

    if (prev instanceof HTMLButtonElement) {
      prev.disabled = atStart || maxScroll <= 0;
      prev.hidden = maxScroll <= 0;
    }
    if (next instanceof HTMLButtonElement) {
      next.disabled = atEnd || maxScroll <= 0;
      next.hidden = maxScroll <= 0;
    }

    const dots = this.refs.dots;
    if (dots instanceof HTMLElement) {
      dots.querySelectorAll('.promo-pc__dot').forEach((dot, index) => {
        const isActive = index === this.#activeIndex;
        dot.classList.toggle('is-active', isActive);
        if (dot instanceof HTMLButtonElement) {
          dot.setAttribute('aria-current', isActive ? 'true' : 'false');
        }
      });

      if (pageCount <= 1) dots.hidden = true;
    }
  }
}

if (!customElements.get('promo-product-carousel')) {
  customElements.define('promo-product-carousel', PromoProductCarousel);
}
